/**
 * Ad account API wrapper. Sync pulls ad accounts from the Facebook Graph API
 * and upserts them server-side.
 */
import { api } from '@/utils/api';
import type { AdAccountDTO, BillingSyncResult, BulkActionResult, SyncResult } from '@shared/types';

/**
 * List ad accounts, optionally scoped to a single linked Facebook account.
 * @param facebookAccountId Optional DB id of the parent Facebook account.
 */
export async function list(facebookAccountId?: string): Promise<AdAccountDTO[]> {
  const response = await api.get<AdAccountDTO[]>('/ad-accounts', {
    params: { facebook_account_id: facebookAccountId },
  });
  return response.data;
}

/**
 * Trigger a sync of ad accounts for a linked Facebook account.
 * @param facebookAccountId DB id of the Facebook account to sync from.
 */
export async function sync(facebookAccountId: string): Promise<SyncResult> {
  const response = await api.post<SyncResult>('/ad-accounts/sync', { facebookAccountId });
  return response.data;
}

/** Refresh card + lifetime spend + spend cap for the user's ad accounts. */
export async function syncBilling(facebookAccountId?: string): Promise<BillingSyncResult> {
  const response = await api.post<BillingSyncResult>('/ad-accounts/sync-billing', { facebookAccountId });
  return response.data;
}

/** Set an ad account's spend limit (0 removes it). */
export async function setSpendCap(id: string, spendCap: number): Promise<AdAccountDTO> {
  const response = await api.post<AdAccountDTO>(`/ad-accounts/${id}/spend-cap`, { spendCap });
  return response.data;
}

/** Set the spend limit on many ad accounts at once (0 removes). */
export async function bulkSpendCap(ids: string[], spendCap: number): Promise<BulkActionResult> {
  const response = await api.post<BulkActionResult>('/ad-accounts/bulk/spend-cap', { ids, spendCap });
  return response.data;
}

export default { list, sync, syncBilling, setSpendCap, bulkSpendCap };
